// POS → Restaurant → Comanda y Estaciones (/pos/rest-estaciones).
// Estaciones de preparación (Cocina caliente/fría/Bar…): a qué estación se
// rutea cada CATEGORÍA de producto (comanda impresa + KDS), y el catálogo de
// MODIFICADORES (grupos con opciones y precio extra) asignables a cada plato.
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Printer, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Campo, Switch, fieldCls, money, posRpc } from './shared';
import { EstacionT, ModGrupoT, useLocalRestaurant } from './restaurante';

const INV = { headers: { 'Content-Profile': 'inventario', 'Accept-Profile': 'inventario' } };

interface CategoriaT { id: string; nombre: string }
interface ProductoLite { id: string; codigo: string; nombre: string; precio_base: number }
interface OpcionEdit { id: string | null; nombre: string; precio_extra: string; eliminar?: boolean }

function ImpresoraCelda({ estacion, onSave }: {
  estacion: EstacionT;
  onSave: (data: { modo_impresion: string; impresora_host: string; impresora_puerto: number }) => void;
}) {
  const [modo, setModo] = useState<string>(estacion.modo_impresion ?? 'terminal');
  const [host, setHost] = useState(estacion.impresora_host ?? '');
  const [puerto, setPuerto] = useState(String(estacion.impresora_puerto ?? 9100));
  return (
    <div className="flex items-center gap-1.5 flex-wrap text-xs">
      <select className={`${fieldCls} !w-auto !h-7 !text-xs`} value={modo}
              onChange={(e) => {
                setModo(e.target.value);
                if (e.target.value === 'terminal') onSave({ modo_impresion: 'terminal', impresora_host: host, impresora_puerto: Number(puerto) || 9100 });
              }}>
        <option value="terminal">Terminal (navegador)</option>
        <option value="red">Red (IP ESC/POS)</option>
        <option value="nombre">Nombre (impresora del agente)</option>
      </select>
      {modo === 'red' && (
        <>
          <input className={`${fieldCls} !w-32 !h-7 !text-xs font-mono`} placeholder="IP impresora"
                 value={host} onChange={(e) => setHost(e.target.value)} />
          <input className={`${fieldCls} !w-16 !h-7 !text-xs font-mono`} value={puerto}
                 onChange={(e) => setPuerto(e.target.value)} />
          <Button size="sm" variant="outline" disabled={!host.trim()}
                  onClick={() => onSave({ modo_impresion: 'red', impresora_host: host.trim(), impresora_puerto: Number(puerto) || 9100 })}>
            <Save className="w-3.5 h-3.5" />
          </Button>
        </>
      )}
      {modo === 'nombre' && (
        <>
          {/* Impresora local/USB de la PC donde corre el print-agent: en Windows
              debe estar COMPARTIDA con este nombre; en Linux, nombre de cola CUPS. */}
          <input className={`${fieldCls} !w-36 !h-7 !text-xs font-mono`} placeholder="ej. COCINA"
                 value={host} onChange={(e) => setHost(e.target.value)} />
          <Button size="sm" variant="outline" disabled={!host.trim()}
                  onClick={() => onSave({ modo_impresion: 'nombre', impresora_host: host.trim(), impresora_puerto: Number(puerto) || 9100 })}>
            <Save className="w-3.5 h-3.5" />
          </Button>
        </>
      )}
    </div>
  );
}

export function EstacionesRestaurant() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { locationId } = useLocalRestaurant();

  const { data: estaciones = [] } = useQuery<EstacionT[]>({
    queryKey: ['rest-estaciones'],
    queryFn: () => posRpc<EstacionT[]>('rest_estaciones_listar', {}),
    staleTime: 0, refetchOnMount: 'always',
  });
  const { data: categorias = [] } = useQuery<CategoriaT[]>({
    queryKey: ['inv-categorias-rest'],
    queryFn: async () => (await apiClient.get('/rest/inventario.categorias?select=id,nombre&order=nombre')).data,
  });
  const { data: grupos = [] } = useQuery<ModGrupoT[]>({
    queryKey: ['rest-mods'],
    queryFn: () => posRpc<ModGrupoT[]>('rest_mods_listar', {}),
    staleTime: 0, refetchOnMount: 'always',
  });

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['rest-estaciones'] });
    void qc.invalidateQueries({ queryKey: ['rest-mods'] });
    void qc.invalidateQueries({ queryKey: ['rest-estado'] });
  };
  const rpc = async (fn: string, body: Record<string, unknown>, ok: string) => {
    try { await posRpc(fn, body); invalidar(); toast.success(ok); return true; }
    catch (e) { toast.error(getErrorMessage(e)); return false; }
  };

  // ---- estaciones ----
  const [nuevaEst, setNuevaEst] = useState('');
  const catDe = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of estaciones) for (const c of e.categorias ?? []) m.set(c.id, e.id);
    return m;
  }, [estaciones]);

  const toggleCat = async (est: EstacionT, cat: CategoriaT) => {
    const actuales = (est.categorias ?? []).map((c) => c.id);
    const next = actuales.includes(cat.id) ? actuales.filter((x) => x !== cat.id) : [...actuales, cat.id];
    await rpc('rest_estacion_categorias_set', { _estacion: est.id, _categorias: next },
      `Ruteo de «${cat.nombre}» actualizado`);
  };

  // ---- modificadores ----
  const [gSel, setGSel] = useState<string | null>(null);
  const [gNombre, setGNombre] = useState('');
  const [gMin, setGMin] = useState('0');
  const [gMax, setGMax] = useState('1');
  const [gOpciones, setGOpciones] = useState<OpcionEdit[]>([]);

  const cargarGrupo = (g: ModGrupoT | null) => {
    setGSel(g?.id ?? null);
    setGNombre(g?.nombre ?? '');
    setGMin(String(g?.min_sel ?? 0));
    setGMax(String(g?.max_sel ?? 1));
    setGOpciones((g?.opciones ?? []).map((o) => ({ id: o.id, nombre: o.nombre, precio_extra: String(o.precio_extra) })));
  };
  const guardarGrupo = () =>
    rpc('rest_mod_grupo_guardar', {
      _data: { nombre: gNombre, min_sel: Number(gMin) || 0, max_sel: Math.max(1, Number(gMax) || 1),
        opciones: gOpciones.filter((o) => o.nombre.trim() || o.eliminar)
          .map((o) => ({ id: o.id, nombre: o.nombre.trim(), precio_extra: Number(o.precio_extra) || 0, eliminar: o.eliminar ?? false })) },
      _id: gSel,
    }, 'Grupo de modificadores guardado').then((ok) => { if (ok && !gSel) cargarGrupo(null); });

  // ---- asignación plato ↔ grupos ----
  const [q, setQ] = useState('');
  const [prod, setProd] = useState<ProductoLite | null>(null);
  const [prodGrupos, setProdGrupos] = useState<string[]>([]);
  const { data: resultados = [] } = useQuery<ProductoLite[]>({
    queryKey: ['rest-prod-buscar', q, locationId],
    queryFn: async () => (await apiClient.post('/rpc/pos_buscar_productos',
      { p_q: q, p_location: locationId || null, p_partner: null }, INV)).data,
    enabled: q.length >= 2,
  });
  const elegirProducto = async (p: ProductoLite) => {
    setProd(p); setQ('');
    try {
      const gs = await posRpc<{ grupo_id: string }[]>('rest_producto_mods', { _producto: p.id });
      setProdGrupos(gs.map((g) => g.grupo_id));
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  return (
    <div className="p-6 space-y-4 max-w-6xl">
      {dlg.ui}
      <h1 className="text-xl font-semibold flex items-center gap-2"><Printer className="w-5 h-5" /> Comanda y Estaciones</h1>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Estaciones de preparación y ruteo por categoría</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Cada categoría de producto se envía a UNA estación (su comanda y su pantalla de cocina).
            Sin asignación, el plato va a la primera estación.
          </p>
          <div className="flex gap-2 items-end flex-wrap">
            <Campo label="Nueva estación (Cocina caliente, Fría, Bar…)">
              <input className={`${fieldCls} !w-64`} value={nuevaEst} onChange={(e) => setNuevaEst(e.target.value)}
                     onKeyDown={(e) => {
                       if (e.key === 'Enter' && nuevaEst.trim()) {
                         void rpc('rest_estacion_guardar', { _data: { nombre: nuevaEst.trim() } }, 'Estación creada');
                         setNuevaEst('');
                       }
                     }} />
            </Campo>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground border-b">
                  <th className="py-2 pr-3 text-right w-10">N°</th>
                  <th className="py-2 pr-3 text-left">Estación</th>
                  <th className="py-2 pr-3 text-left">Categorías que rutea</th>
                  <th className="py-2 pr-3 text-center">Imprime comanda</th>
                  <th className="py-2 pr-3 text-left">Impresora</th>
                  <th className="py-2 pr-3" />
                </tr>
              </thead>
              <tbody>
                {estaciones.map((e, i) => (
                  <tr key={e.id} className="border-b last:border-0 align-top">
                    <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3 font-medium">{e.nombre}</td>
                    <td className="py-2 pr-3">
                      <div className="flex gap-1 flex-wrap">
                        {categorias.map((c) => {
                          const asignadaAqui = (e.categorias ?? []).some((x) => x.id === c.id);
                          const asignadaOtra = !asignadaAqui && catDe.has(c.id);
                          return (
                            <button key={c.id} onClick={() => void toggleCat(e, c)}
                              className={`px-2 py-0.5 rounded-full border text-xs ${asignadaAqui
                                ? 'bg-primary text-primary-foreground border-primary'
                                : asignadaOtra ? 'opacity-40' : ''}`}
                              title={asignadaOtra ? 'Asignada a otra estación (clic para moverla aquí)' : ''}>
                              {c.nombre}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-center">
                      <Switch checked={e.imprime_comanda} label=""
                              onChange={(v) => void rpc('rest_estacion_guardar',
                                { _data: { imprime_comanda: v }, _id: e.id }, 'Estación actualizada')} />
                    </td>
                    <td className="py-2 pr-3">
                      <ImpresoraCelda estacion={e} onSave={(data) =>
                        void rpc('rest_estacion_guardar', { _data: data, _id: e.id }, 'Impresora actualizada')} />
                    </td>
                    <td className="py-2 pr-3 text-right">
                      <Button size="sm" variant="ghost" onClick={async () => {
                        if (await dlg.confirmar(`¿Eliminar la estación "${e.nombre}"?`, { peligro: true }))
                          void rpc('rest_estacion_guardar', { _data: {}, _id: e.id, _eliminar: true }, 'Estación eliminada');
                      }}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </td>
                  </tr>
                ))}
                {!estaciones.length && (
                  <tr><td colSpan={6} className="py-4 text-center text-muted-foreground">
                    Crea al menos una estación (p.ej. «Cocina») para que las comandas tengan destino.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Modificadores (grupos y opciones)</CardTitle>
              <Button size="sm" variant="outline" onClick={() => cargarGrupo(null)}><Plus className="w-4 h-4 mr-1" /> Nuevo grupo</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-1.5 flex-wrap">
              {grupos.map((g) => (
                <button key={g.id} onClick={() => cargarGrupo(g)}
                  className={`px-2.5 py-1 rounded-full border text-sm ${gSel === g.id ? 'border-primary text-primary font-medium' : ''}`}>
                  {g.nombre} <span className="text-xs text-muted-foreground">({g.opciones.length})</span>
                </button>
              ))}
            </div>
            <div className="border rounded-md p-3 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <Campo label="Nombre del grupo *">
                  <input className={fieldCls} placeholder="Término, Extras, Sin…" value={gNombre} onChange={(e) => setGNombre(e.target.value)} />
                </Campo>
                <Campo label="Mín. a elegir (0 = opcional)">
                  <input className={fieldCls} type="number" min="0" value={gMin} onChange={(e) => setGMin(e.target.value)} />
                </Campo>
                <Campo label="Máx. a elegir">
                  <input className={fieldCls} type="number" min="1" value={gMax} onChange={(e) => setGMax(e.target.value)} />
                </Campo>
              </div>
              {gOpciones.filter((o) => !o.eliminar).map((o, i) => (
                <div key={o.id ?? `n${i}`} className="grid grid-cols-[1fr_120px_auto] gap-2 items-center">
                  <input className={fieldCls} placeholder="Opción (p.ej. Sin cebolla)" value={o.nombre}
                         onChange={(e) => setGOpciones((os) => os.map((x) => x === o ? { ...x, nombre: e.target.value } : x))} />
                  <input className={`${fieldCls} text-right font-mono`} type="number" step="0.01" min="0"
                         placeholder="+ precio" value={o.precio_extra}
                         onChange={(e) => setGOpciones((os) => os.map((x) => x === o ? { ...x, precio_extra: e.target.value } : x))} />
                  <Button size="sm" variant="ghost" onClick={() =>
                    setGOpciones((os) => o.id ? os.map((x) => x === o ? { ...x, eliminar: true } : x) : os.filter((x) => x !== o))}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ))}
              <div className="flex justify-between">
                <Button size="sm" variant="outline"
                        onClick={() => setGOpciones((os) => [...os, { id: null, nombre: '', precio_extra: '0' }])}>
                  <Plus className="w-4 h-4 mr-1" /> Opción
                </Button>
                <div className="flex gap-2">
                  {gSel && (
                    <Button size="sm" variant="ghost" onClick={async () => {
                      if (await dlg.confirmar('¿Eliminar el grupo completo?', { peligro: true }))
                        void rpc('rest_mod_grupo_guardar', { _data: {}, _id: gSel, _eliminar: true }, 'Grupo eliminado')
                          .then(() => cargarGrupo(null));
                    }}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  )}
                  <Button size="sm" onClick={() => void guardarGrupo()} disabled={!gNombre.trim()}>
                    <Save className="w-4 h-4 mr-1" /> Guardar grupo
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Modificadores por plato</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Campo label="Buscar plato/producto">
              <input className={fieldCls} placeholder="Nombre o código…" value={q} onChange={(e) => setQ(e.target.value)} />
            </Campo>
            {q.length >= 2 && (
              <div className="max-h-40 overflow-auto border rounded-md divide-y text-sm">
                {resultados.map((p) => (
                  <button key={p.id} className="w-full text-left px-3 py-1.5 flex justify-between"
                          onClick={() => void elegirProducto(p)}>
                    <span>{p.nombre} <span className="text-xs text-muted-foreground">{p.codigo}</span></span>
                    <span className="font-mono">{money(p.precio_base)}</span>
                  </button>
                ))}
              </div>
            )}
            {prod && (
              <div className="border rounded-md p-3 space-y-2">
                <div className="font-medium">{prod.nombre}</div>
                <div className="flex gap-1.5 flex-wrap">
                  {grupos.filter((g) => g.activo !== false).map((g) => {
                    const on = prodGrupos.includes(g.id!);
                    return (
                      <button key={g.id} onClick={() => setProdGrupos((s) => on ? s.filter((x) => x !== g.id) : [...s, g.id!])}
                        className={`px-2.5 py-1 rounded-full border text-sm ${on ? 'bg-primary text-primary-foreground border-primary' : ''}`}>
                        {g.nombre}
                      </button>
                    );
                  })}
                  {!grupos.length && <span className="text-sm text-muted-foreground">Primero crea grupos de modificadores.</span>}
                </div>
                <div className="flex justify-end">
                  <Button size="sm" onClick={() => void rpc('rest_producto_mods_set',
                    { _producto: prod.id, _grupos: prodGrupos }, `Modificadores de «${prod.nombre}» guardados`)}>
                    <Save className="w-4 h-4 mr-1" /> Guardar asignación
                  </Button>
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Tip: los platos preparados deben ser productos con «controla stock» APAGADO (no descargan kardex);
              las bebidas embotelladas sí lo descuentan como siempre.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
